import { Link, graphql } from "gatsby"
import React from "react"
import { Layout, Head as HeadComponent } from "@bw/layouts"
import { Section, Spacer } from "@bw/bits"
import { Cartouche } from "@bw/partials"
import { PillMarker } from "@bw/icons"
import { useTranslation } from "react-i18next"
import { PageHeader } from "@bw/modules"
import EventModel from "models/event"
import { Content } from "@bw/modules/mapModules"
import { useBreadcrumbs } from "../hooks/useBreadcrumbs"

export const Head = ({ location, pageContext, data }) => (
  <HeadComponent
    title={data.page.metaTitle || data.page.title}
    image={
      data.page.image?.localImage?.childImageSharp?.original?.src ??
      data.page.teaserImage?.localImage?.childImageSharp?.original?.src
    }
    data={data}
    location={location}
    pageContext={pageContext}
    description={data.page.metaDescription}
    doNotIndex={data.page.doNotIndex}
  />
)

const EventTemplate = ({ data, pageContext, location }) => {
  const { t } = useTranslation()
  const { page, parents, mount } = data
  const Event = new EventModel(page)
  const Pois = Event.getPois()

  const breadcrumbsLinks = useBreadcrumbs({
    mount,
    page,
    parents: pageContext.parents.map(id =>
      parents.nodes.find(parent => parent.id === id)
    ),
  })

  let dates = Event.getDateSpan()
  dates = dates.map((date, i) => (
    <div>
      {dates.length === 2 && i === 0
        ? t("dates:fromDate", { date })
        : dates.length === 2 && i === 1
        ? t("dates:toDate", { date })
        : date}
    </div>
  ))

  return (
    <Layout {...{ pageContext, location }}>
      <PageHeader
        suptitle={dates}
        title={Event.title}
        breadcrumbs={breadcrumbsLinks}
      />
      {Pois.length > 0 && (
        <Section>
          <Cartouche>
            {Pois.map((Poi, i) => (
              <Link to={Poi.url} target="_blank" rel="noreferrer">
                <Spacer direction="row">
                  <PillMarker />
                  <div>
                    <div>
                      <b>{t("job.locationTitle")}</b>
                    </div>
                    {Poi.title}
                  </div>
                </Spacer>
              </Link>
            ))}
          </Cartouche>
        </Section>
      )}
      {/*
       * Loading the Contents modules, ex Content or CallToAction, TeasersLatest
       */}
      <Content modules={page.content} />
    </Layout>
  )
}

export default EventTemplate

export const eventQuery = graphql`
  query eventQuery($id: String!, $parents: [String]) {
    parents: allCollectionPages(filter: { id: { in: $parents } }) {
      nodes {
        id
        to: slug
        title
      }
    }

    page: collectionEvents(id: { eq: $id }) {
      title
      metaTitle
      metaDescription
      image {
        localImage {
          childImageSharp {
            original {
              src
            }
          }
        }
      }
      doNotIndex

      teaserImage {
        localImage {
          childImageSharp {
            original {
              src
            }
          }
        }
      }

      startDate
      startTime
      endDate
      endTime

      poi {
        id
        url
        title
      }

      content {
        __typename
        suptitle
        title
        subtitle
        backgroundVariant
        buttonType {
          value
        }
        buttonUrl
        buttonIsExternal
        buttonOpenInModal
        buttonText
        buttonPosition {
          value
        }
        buttonEntry {
          url
        }
        idAttribute
        classAttribute

        ...AudioModuleFragment
        ...CallToActionModuleFragment
        ...ContentModuleFragment
        ...ContentWithImageModuleFragment
        ...GalleryModuleFragment
        ...ContentOnColumnsModuleFragment
        ...DownloadsModuleFragment
        ...TeasersManualModuleFragment
        ...LogoGridModuleFragment
        ...ImageModuleFragment
        ...VideoModuleFragment
        ...FormModuleFragment
        ...ListOfPoisModuleFragment
        ...ListOfArticlesModuleFragment
        ...ListOfProfilesModuleFragment
        ...ListOfJobsModuleFragment
        ...ListOfEventsModuleFragment

        ...LinksModuleFragment
        ...PredefinedModuleModuleFragment
      }
    }

    mount: collectionPages(mount: { handle: { eq: "events" } }) {
      slug
      title
    }
  }
`
